import { Oprf, VOPRFClient, Evaluation } from '@cloudflare/voprf-ts';
import {
  type TopicAwareness,
  type TopicAwarenessMap,
  type TopicAwarenessResult,
} from '../topic_awareness';

export type { TopicAwareness, TopicAwarenessMap, TopicAwarenessResult };
export { TopicAwarenessLogic } from '../topic_awareness';

/**
 * Minimal interface for duck typing: any object that can expose its phone number as a string.
 */
export interface PhoneLike {
  toString(): string;
}

export interface ContactLike {
  name: string;
  phone: string | PhoneLike;
  userId?: string; // Pre-resolved server user ID; skips OPRF when present
}

function toPhoneString(phone: string | PhoneLike): string {
  return typeof phone === 'string' ? phone : phone.toString();
}

export type MessageIntent = 'intro' | 'reminder' | 'update';
/** A leg's place in the shape of its sharing run — the indefinite propagation
 * of a topic (#455, #576). `start` is the empty starting leg created at the head of
 * every sharing flow, whatever the topic type: it reaches nobody and carries no
 * intent. `share` is a leg recording a batch of messages with a particular intent (an intro,
 * reminder or update). `reshare` passes a topic on one more degree,
 * carrying the intent of the share it passes on. */
export type LegType = 'start' | 'share' | 'reshare';

/**
 * The object returned at leg initiation and by
 * GET /api/sharing-legs/{id}/reshare-story (#447). Mirrors ReshareInfoPayload in
 * internal/share/reshare_info.go. Goal state is client-derived from impressionGoal vs
 * runImpressionCount.
 */
export interface ReshareInfoLike {
  startingLegId: string;
  /** The starting leg sender's allowed detail; start-prefixed like the other
   * start-sourced fields, since any hop may carry its own. */
  startingAllowedDetail: string;
  startingAllowedSenderName: string;
  startingSenderPronouns: string;
  /** The queried leg's OWN reshare story line (#447) — its allowed name and, for a
   * non-starting leg, the sender's ORIGINAL first-person connection as stored
   * ("I work out with"). Lets a reshare recipient build the next-hop preview
   * without the auth-gated leg DTO. */
  allowedSenderName: string;
  connectionToPrevLeg: string;
  impressionGoal?: number;
  runImpressionCount: number;
  topicLimitReached: boolean;
  degreesOfSeparation: number;
  /** Relativize(connectionToPrevLeg), computed server-side at read time so the
   * client never duplicates the Go grammar ("works out with"). Empty for the
   * starting leg. Mirrors ReshareInfoPayload.ConnectionToPrevLegRelativized. */
  connectionToPrevLegRelativized: string;
  /** The queried leg's reshare story in its sender's voice, shaped by the
   * topic's reshare story settings: who they got it from and how they know
   * them, how far that person is from the starting sender, and the senders'
   * allowed details when the settings include them (`I reshared this from my
   * friend Fred. He's 2 degrees separated from Sally, the author.`). Empty
   * when the queried leg is the starting leg. */
  story: string;
  /** The starting leg's sent_at (ISO 8601) — when the originator started the run. */
  startedAt: string;
  /** True when the underlying by-invitation-only topic isn't readable by this
   * caller (#447): every content field above is masked (empty), so the client
   * shows a "verify to view" skeleton instead of the note/originator. */
  gated?: boolean;
}

export interface UserWithTail {
  id: string;
  phoneOprf?: string;
  phoneTail?: string;
}

/**
 * Minimal, non-sensitive metadata for a by-invitation-only topic the caller
 * cannot yet read, returned by the capability-authorized invite-preview endpoint.
 */
export interface InvitePreview {
  type: string;
  image: string | null;
}

export interface DeterministicHashResult {
  hash: string;
  phoneTail: string;
}

/**
 * The result of looking up a phone number's profile. `checkPhoneExists` has to
 * compute the number's OPRF to do the lookup, so it hands it back (along with
 * the tail) — callers that then create a profile or request a migration can
 * reuse it instead of hashing the same number a second time.
 */
export interface PhoneLookupResult {
  /** id of the profile that owns this number, or null if none does. */
  userId: string | null;
  /** The number's OPRF hash and last-two digits, computed during the lookup. */
  phoneOprf: string;
  phoneTail: string;
}

/**
 * Cooperative-scheduling controls for the batched OPRF hash. The blind/finalize
 * elliptic-curve work is synchronous JS, so hashing a whole address book at once
 * would freeze the UI thread; these let the caller watch progress and abort.
 */
export interface HashProgressOptions {
  /**
   * Invoked after each chunk with the running count of phones hashed so far and
   * the total to hash (called once with (0, total) up front). Drives a progress
   * indicator.
   */
  onProgress?: (done: number, total: number) => void;
  /**
   * When aborted (e.g. the user closes the Share Wizard), hashing stops at the
   * next chunk boundary and the promise rejects instead of grinding through the
   * rest of the address book.
   */
  signal?: AbortSignal;
  /**
   * Called after each chunk with just that chunk's freshly-computed hashes.
   * Return `true` to stop early (the promise resolves with the partial map).
   * Lets a PSI caller quit the moment it has found everything it was looking
   * for, so it doesn't keep paying for OPRF over the rest of the address book.
   */
  onChunkComplete?: (chunk: Map<string, DeterministicHashResult>) => boolean;
  /**
   * Reports the total synchronous blind+finalize CPU time (ms) this call spent,
   * measured across all chunks and excluding the network round-trip. For dev
   * diagnostics only — this is the elliptic-curve cost that would jank the UI.
   */
  onCpuTime?: (ms: number) => void;
}

/** One row the server writes per recipient of a leg it accepts. */
export interface MessageReceipt {
  id: string;
  topic_id: string;
  recipient_id: string;
  sender_id?: string;
  intent: MessageIntent;
  sent_at: string;
}

/**
 * One entry for the batch ensure (get-or-create) endpoint. `lastKnownUserId` is
 * an optional hint: when set, the server confirms that id still exists before
 * falling back to an upsert by `phoneOprf` (used to migrate a stale id); without
 * it, the entry is a pure get-or-create.
 */
export interface EnsureUsersRequestItem {
  lastKnownUserId?: string;
  phoneTail: string;
  phoneOprf: string;
}

/** Self-contained addressing header for a leg. Parallel to MessageHeader in @/modules/message. */
export interface MessageHeaderLike {
  topicId: string;
  /** The intent of a leg that is actually texted to someone. A start leg has
   * no header: the starting leg is created by its own call (initiateSharingRun),
   * reaches nobody, and addresses no one. Every leg that does need one is a
   * child of that starting leg. */
  intent: MessageIntent;
  /** Where this leg sits in its run: a `share` off the sender's own starting
   * leg, or a `reshare` passing on the leg named by `previousLegId`. An intro
   * is either one — the same message composed in a different sharing mode —
   * so the caller says which rather than this being read off the intent. */
  type: Extract<LegType, 'share' | 'reshare'>;
  recipients: ContactLike[];
  /** Update-intent only (#427): the sender's ≤100-char summary of what changed. */
  updateSummary?: string;
  /** Update-intent only (#427): ISO timestamp of the head of the change list. */
  aboutChangesSince?: string;
  /** Reshares only (#447): the name the sender agreed to propagate. */
  allowedSenderName?: string;
  /** Reshares only (#447): the sender's first-person connection to the
   * previous leg's sender, sent as picked ("I work with") and stored verbatim —
   * the backend relativizes at read time. */
  connectionToPrevLeg?: string;
  /** Where this leg came from, accepted on any intent: the leg being
   * reshared from for a reshare, and otherwise the start leg of the
   * sender's own sharing flow. */
  previousLegId?: string;
  /** The sender's own extra detail for the accumulating sharing story.
   * Optional on any intent. */
  allowedDetail?: string;
  /** The sender's preferred pronouns, used to reconstruct the sharing story.
   * Optional on any intent. */
  senderPronouns?: string;
}

/** Self-contained leg shape returned by POST /api/sharing-legs. Avoids circular imports. */
export interface SharingLegLike {
  id: string;
  previousLegId?: string;
  topic_id: string;
  sender_id?: string;
  type: LegType;
  /** Absent on a start leg, which carries no message. */
  intent?: string;
  status: string;
  sent_at: string;
  impression_count: number;
  click_thru_count: number;
  // camelCase on the wire — the convention for fields added from #427 onward.
  updateSummary?: string;
  aboutChangesSince?: string;
  // The name the sender agreed to propagate and the
  // sender's first-person connection to the previous leg's sender, stored as
  // picked (relativized at read time).
  allowedSenderName?: string;
  connectionToPrevLeg?: string;
  allowedDetail?: string;
  senderPronouns?: string;
}

/**
 * One entry of the labeled-PSI server set for Share Updates prepopulation
 * (#427): a recipient the current user previously messaged about an topic.
 */
export interface UpdateCandidateLike {
  userId: string;
  phoneOprf: string;
}

/** A local contact matched (via PSI) to a previously messaged recipient. */
export interface PastRecipientMatch {
  contact: ContactLike;
  userId: string;
}

export interface PastRecipientsResult {
  matches: PastRecipientMatch[];
  /** phone → server userId, for caching resolved IDs back onto contacts. */
  resolvedIds: Map<string, string>;
}

/** The update leg that brought the current user to an topic, if any (#427). */
export interface ReceivedUpdateLike {
  /** The update leg the recipient arrived on, which a reshare passes on. */
  legId: string;
  updateSummary?: string;
  aboutChangesSince?: string;
  sentAt: string;
}

export interface InitiateSharingLegResponse {
  success: boolean;
  leg: SharingLegLike;
  messages: MessageReceipt[];
  recipient_count: number;
  recipients: string[];
  replacedUserIds?: Record<string, string>;
  createdUserIds?: Record<string, string>; // phone → userId for newly-created users
  /** The precomputed reshare info, if available. */
  reshareInfo?: ReshareInfoLike;
}

/**
 * One leg of a story, described for the story endpoints whether or not it
 * exists in storage. Mirrors what a stored leg records about its sender
 * (the app's `LegStoryInfo`): the name they let travel with the story, their
 * first-person connection to the leg before, their pronouns, and — when they
 * start the run — the note the story quotes. Every field is the sender's to
 * withhold, so every field is optional; the server fills in the defaults it
 * uses for stored legs.
 */
export interface LegStoryInfoLike {
  allowedDetail?: string;
  senderPronouns?: string;
  allowedSenderName?: string;
  connectionToPrevLeg?: string;
}

/**
 * Hypothetical legs read on from a real one, `previousLegId`, whose topic's
 * reshare story settings shape the story. The legs are in chronological
 * order: the first passes `previousLegId` on, and the last is the leg whose
 * story is wanted.
 */
export interface StoryRequest {
  futureLegsNearToFar: readonly LegStoryInfoLike[];
  previousLegId: string;
}

/**
 * Hypothetical legs alone, in chronological order: the first starts the run
 * and the last is the leg whose story is wanted. Told by
 * `sharingSettings`: reshare story settings in the shape a topic stores them.
 */
export interface FictionalStoryRequest {
  futureLegsNearToFar: readonly LegStoryInfoLike[];
  sharingSettings: object;
}

export interface MePrFoServiceRoutes {
  publicKey: string;
  evaluate: string;
  usersByTails: string;
  usersByOprfs: string;
  users: string;
  ensure: string;
  legs: string;
  legById: (id: string) => string;
  legTopicById: (id: string) => string;
  invitePreviewById: (id: string) => string;
  topicAwareness: (topicId: string) => string;
  getPastShareRecipientsForTopic: (topicId: string, before?: string) => string;
  receivedUpdate: (topicId: string) => string;
  story: string;
  fictionalStory: string;
}

export interface MePrFoServiceDependencies {
  /**
   * Function used to contact the backend. It should automatically handle authentication headers.
   */
  apiCall: (path: string, init?: RequestInit) => Promise<Response>;
  /**
   * Optional overrides for backend endpoint routes.
   */
  routes?: Partial<MePrFoServiceRoutes>;
  /**
   * Optional logger override (defaults to console).
   */
  logger?: Pick<Console, 'log' | 'error'>;
}

export interface MePrFoService {
  computeDeterministicHash(
    phoneNumber: string,
    opts?: HashProgressOptions
  ): Promise<DeterministicHashResult>;
  /**
   * Batched variant: blinds and finalizes in bounded chunks (one evaluate
   * round-trip per chunk), yielding to the event loop between chunks so the
   * synchronous elliptic-curve work never freezes the UI thread for a large
   * address book. Keyed by phone number. Pass `onProgress`/`signal` to watch
   * progress or abort.
   */
  computeDeterministicHashes(
    phoneNumbers: string[],
    opts?: HashProgressOptions
  ): Promise<Map<string, DeterministicHashResult>>;
  /**
   * Labeled OPRF-PSI (#427): intersects the given contacts with the recipients
   * the current user previously messaged about the topic. Contacts with a
   * cached userId short-circuit; the rest are batch-hashed locally and matched
   * against the candidates' phoneOprf values. The address book never leaves
   * the device.
   *
   * `before` (the change-list head) scopes the candidate set server-side to
   * recipients last messaged before it, so the client hashes against a smaller
   * set (and skips hashing entirely when nothing needs updating).
   */
  matchMyPastRecipients(
    topicId: string,
    contacts: ContactLike[],
    before?: string,
    opts?: HashProgressOptions
  ): Promise<PastRecipientsResult>;
  /**
   * The update leg that brought the current user to the topic (via their
   * pending-invite session claim or their message history), or null (#427).
   */
  getReceivedUpdate(topicId: string): Promise<ReceivedUpdateLike | null>;
  checkPhoneExists(phoneNumber: string): Promise<PhoneLookupResult>;
  initiateSharingLeg(
    header: MessageHeaderLike
  ): Promise<InitiateSharingLegResponse>;
  /**
   * Start a sharing run: create its empty starting leg (#455), a start leg reaching
   * nobody (impression_count 0) that carries whichever sharing-story
   * contributions the sender made. A sharing run is the indefinite propagation
   * of a topic; one sharing flow — one trip through the share wizard — starts
   * one run and sends its first degree, so every leg texted during the flow
   * is a child of this starting leg and texting N people is one run, not N. Every topic
   * type starts one. Takes no recipients, so it skips the OPRF resolution
   * initiateSharingLeg does.
   */
  initiateSharingRun(
    topicId: string,
    story?: {
      allowedSenderName?: string;
      allowedDetail?: string;
      senderPronouns?: string;
    }
  ): Promise<InitiateSharingLegResponse>;
  markLegAsSent(legId: string): Promise<void>;
  markLegAsInProgress(legId: string): Promise<void>;
  getSharingLeg(legId: string): Promise<SharingLegLike | null>;
  /**
   * The precomputed reshare payload for a leg (#447), or null when the
   * leg doesn't exist or there aren't any reshare info.
   */
  getReshareInfoForLeg(legId: string): Promise<ReshareInfoLike | null>;
  /**
   * The reshare info payload for legs the client describes — the one being
   * composed, or made-up ones for a preview — read on from the real leg
   * `previousLegId`, rendered by the same server code that renders a stored
   * leg's, so the client composes no story grammar of its own. Resolves to a
   * gated payload when that leg's topic is one this caller may not read.
   */
  renderReshareStory(request: StoryRequest): Promise<ReshareInfoLike>;
  /**
   * The reshare info payload for described legs alone, the first of them
   * starting the run, told by the request's sharing settings rather than any
   * topic's. Rendered by the same server code as `renderReshareStory`.
   */
  renderReshareFictionalStory(
    request: FictionalStoryRequest
  ): Promise<ReshareInfoLike>;
  lookupLegTopicId(legId: string): Promise<string | null>;
  recordLegClickThru(legId: string): Promise<void>;
  getInvitePreview(topicId: string): Promise<InvitePreview | null>;
  /**
   * Batch get-or-create: resolves each request to a server user id, returning a
   * map from phone hash → user id. An entry's optional `lastKnownUserId` is confirmed
   * first (used to migrate a stale id); an entry without one is a pure create.
   * The single primitive behind recipient creation, the send-time stale-id
   * resync, and the background sync's daily re-confirm.
   */
  ensureUsers(reqs: EnsureUsersRequestItem[]): Promise<Map<string, string>>;
  /**
   * Resolve many users by their exact OPRF phone hashes in one batch lookup — an
   * indexed point lookup per hash — returning a map from phone hash → user id for
   * the hashes that hold a user (hashes with no user are simply absent). Never
   * creates. The batch counterpart used to resolve a whole recipient set (or a
   * tick of the background contact sync) to server ids.
   */
  getUserIdsByOprf(phoneOprfs: string[]): Promise<Map<string, string>>;

  /** @internal */
  _fetchUsersByTails(tails: string[]): Promise<UserWithTail[]>;
  /**
   * Resolve a recipient set to server user ids, get-or-creating any that aren't
   * pre-resolved. Contacts with a cached `userId` pass through; the rest are
   * OPRF-hashed locally and ensured in one batch call. Returns the deduped id
   * list plus a phone → id map for the freshly-resolved (uncached) contacts, so
   * callers can cache the ids back onto contacts.
   * @internal
   */
  _ensureContactsResolved(contacts: ContactLike[]): Promise<{
    userIds: string[];
    resolvedByPhone: Map<string, string>;
  }>;
  /**
   * Rebuild a recipient list after an `unknown_user_ids` 422: re-ensure the stale
   * contacts (passing their stale id as the `lastKnownUserId` hint) in one batch call,
   * swap in the fresh ids, drop any that couldn't be resynced, and report the
   * stale → fresh mapping for contact-cache updates.
   * @internal
   */
  _resyncStaleContacts(
    contacts: ContactLike[],
    unknownUserIds: string[]
  ): Promise<{
    updatedContacts: ContactLike[];
    replacedUserIds: Record<string, string>;
  }>;
  /** @internal */
  checkTopicAwareness(
    topicId: string,
    contacts: ContactLike[]
  ): Promise<TopicAwarenessResult>;
}

const DEFAULT_ROUTES: MePrFoServiceRoutes = {
  publicKey: '/api/meprfo/public-key',
  evaluate: '/api/meprfo/evaluate',
  usersByTails: '/api/users/by-tails',
  usersByOprfs: '/api/users/by-oprfs',
  users: '/api/users',
  ensure: '/api/users/ensure',
  legs: '/api/sharing-legs',
  legById: (id: string) => `/api/sharing-legs/${id}`,
  legTopicById: (id: string) => `/api/sharing-legs/${id}/topic`,
  invitePreviewById: (id: string) =>
    `/api/topics/${encodeURIComponent(id)}/invite-preview`,
  topicAwareness: (topicId: string) =>
    `/api/topics/${encodeURIComponent(topicId)}/awareness`,
  getPastShareRecipientsForTopic: (topicId: string, before?: string) =>
    `/api/topics/${encodeURIComponent(topicId)}/past-share-recipients` +
    (before ? `?before=${encodeURIComponent(before)}` : ''),
  receivedUpdate: (topicId: string) =>
    `/api/topics/${encodeURIComponent(topicId)}/received-update`,
  story: '/api/sharing-legs/story',
  fictionalStory: '/api/sharing-legs/fictional-story',
};

const OPRF_SUITE = Oprf.Suite.P256_SHA256;

// How many phones each batched-OPRF chunk blinds/finalizes before yielding the
// main thread. Small enough that one chunk's synchronous elliptic-curve work is
// imperceptible; large enough to keep the per-chunk evaluate round-trips modest.
const HASH_CHUNK_SIZE = 50;

// Yield a macrotask so the browser can paint and process input (e.g. a tap on
// the wizard's close button) between OPRF chunks. A macrotask (setTimeout) — not
// a microtask — is required: microtasks drain before the event loop regains
// control, so they wouldn't let a queued click through.
function yieldToEventLoop(): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, 0));
}

export function createMePrFoService(
  deps: MePrFoServiceDependencies
): MePrFoService {
  const { apiCall } = deps;
  const routes: MePrFoServiceRoutes = { ...DEFAULT_ROUTES, ...deps.routes };
  const logger = deps.logger ?? console;
  const error = logger.error?.bind(logger) ?? (() => undefined);

  let cachedPublicKeyPromise: Promise<Uint8Array> | null = null;

  async function fetchServerPublicKey(): Promise<Uint8Array> {
    if (!cachedPublicKeyPromise) {
      cachedPublicKeyPromise = doFetchServerPublicKey();
      cachedPublicKeyPromise.catch(() => {
        cachedPublicKeyPromise = null;
      });
    }
    return cachedPublicKeyPromise;
  }

  async function doFetchServerPublicKey(): Promise<Uint8Array> {
    try {
      const response = await apiCall(routes.publicKey);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch OPRF public key from server: ${response.status} ${errorText}`
        );
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(
          `Invalid response type: ${contentType}. Response: ${text.substring(0, 200)}`
        );
      }

      const data = await response.json();
      if (!data || !data.publicKey) {
        throw new Error(
          `Invalid response format: missing publicKey field. Response: ${JSON.stringify(data)}`
        );
      }

      const publicKeyHex = data.publicKey as string;
      const publicKeyBytes = new Uint8Array(publicKeyHex.length / 2);
      for (let i = 0; i < publicKeyBytes.length; i++) {
        publicKeyBytes[i] = parseInt(publicKeyHex.substr(i * 2, 2), 16);
      }

      return publicKeyBytes;
    } catch (err) {
      throw new Error(
        `Failed to fetch server public key: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  }

  async function requestServerEvaluation(
    blindedHex: string
  ): Promise<Uint8Array> {
    try {
      const response = await apiCall(routes.evaluate, {
        method: 'POST',
        body: JSON.stringify({ blinded: blindedHex }),
      });

      if (!response.ok) {
        throw new Error('Failed to evaluate blinded element');
      }

      const data = await response.json();
      const evaluatedHex: string = data.evaluated;

      const evaluatedBytes = new Uint8Array(evaluatedHex.length / 2);
      for (let i = 0; i < evaluatedBytes.length; i++) {
        evaluatedBytes[i] = parseInt(evaluatedHex.substr(i * 2, 2), 16);
      }

      return evaluatedBytes;
    } catch (err) {
      throw new Error(
        `Failed to request evaluation: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  }

  async function computeDeterministicHashes(
    phoneNumbers: string[],
    opts: HashProgressOptions = {}
  ): Promise<Map<string, DeterministicHashResult>> {
    const { onProgress, signal, onChunkComplete, onCpuTime } = opts;
    // De-dupe while preserving order; validate before any network round-trip.
    const uniquePhones = Array.from(new Set(phoneNumbers));
    for (const phoneNumber of uniquePhones) {
      if (!/^\d{10}$/.test(phoneNumber)) {
        throw new Error('Phone number must be exactly 10 digits');
      }
    }
    const results = new Map<string, DeterministicHashResult>();
    const total = uniquePhones.length;
    if (total === 0) {
      return results;
    }

    try {
      const publicKeyBytes = await fetchServerPublicKey();
      const client = new VOPRFClient(OPRF_SUITE, publicKeyBytes);
      const encoder = new TextEncoder();

      onProgress?.(0, total);

      // Sum of the synchronous blind+finalize time across chunks (the
      // elliptic-curve CPU cost), excluding the network evaluate round-trip.
      let cpuMs = 0;

      // Blind → evaluate → finalize one chunk at a time. Each chunk is a single
      // DLEQ-verified round-trip, but the blind/finalize elliptic-curve math is
      // synchronous JS — running it over the whole address book at once locks up
      // the main thread. Chunking bounds each synchronous burst, and yielding a
      // macrotask between chunks keeps the UI responsive (progress paints, and
      // the user can close the wizard, which aborts via `signal`).
      for (let start = 0; start < total; start += HASH_CHUNK_SIZE) {
        if (signal?.aborted) {
          throw new Error('aborted');
        }
        const chunk = uniquePhones.slice(start, start + HASH_CHUNK_SIZE);
        const inputs = chunk.map(phone => encoder.encode(phone));

        const blindStart = performance.now();
        const [finData, evaluationRequest] = await client.blind(inputs);
        cpuMs += performance.now() - blindStart;

        const serializedRequest = evaluationRequest.serialize();
        const blindedHex = Array.from(serializedRequest as Uint8Array)
          .map(b => b.toString(16).padStart(2, '0'))
          .join('');

        const evaluatedBytes = await requestServerEvaluation(blindedHex);
        const evaluation = Evaluation.deserialize(OPRF_SUITE, evaluatedBytes);

        const finalizeStart = performance.now();
        const finalHashes = await client.finalize(finData, evaluation);
        cpuMs += performance.now() - finalizeStart;

        const chunkResults = new Map<string, DeterministicHashResult>();
        chunk.forEach((phoneNumber, i) => {
          const hash = Array.from(finalHashes[i] as Uint8Array)
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');
          const entry = { hash, phoneTail: phoneNumber.slice(-2) };
          results.set(phoneNumber, entry);
          chunkResults.set(phoneNumber, entry);
        });

        onProgress?.(Math.min(start + HASH_CHUNK_SIZE, total), total);
        // Let the caller bail as soon as it has what it needs — no point
        // hashing the rest of the address book once every target is found.
        if (onChunkComplete?.(chunkResults)) {
          onCpuTime?.(cpuMs);
          return results;
        }
        await yieldToEventLoop();
      }
      onCpuTime?.(cpuMs);
      return results;
    } catch (err) {
      throw new Error(
        `Failed to compute hash: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  }

  async function computeDeterministicHash(
    phoneNumber: string,
    opts?: HashProgressOptions
  ): Promise<DeterministicHashResult> {
    const results = await service.computeDeterministicHashes(
      [phoneNumber],
      opts
    );
    const result = results.get(phoneNumber);
    if (!result) {
      throw new Error('Failed to compute hash: no result for phone number');
    }
    return result;
  }

  async function fetchUsersByTails(tails: string[]): Promise<UserWithTail[]> {
    const uniqueTails = tails.filter(
      (tail, index) => tails.indexOf(tail) === index
    );
    const tailsParam = uniqueTails.join(',');

    try {
      const response = await apiCall(
        `${routes.usersByTails}?tails=${encodeURIComponent(tailsParam)}`
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Server returned status ${response.status}: ${errorText || 'No error details'}`
        );
      }

      const data = await response.json();
      return data.users || [];
    } catch (err) {
      if (err instanceof TypeError) {
        throw new Error(
          `Network error: Cannot reach server. Check your connection.`
        );
      }
      throw err;
    }
  }

  async function ensureContactsResolved(contacts: ContactLike[]): Promise<{
    userIds: string[];
    resolvedByPhone: Map<string, string>;
  }> {
    const userIds: string[] = [];
    const resolvedByPhone = new Map<string, string>();

    // Contacts with a pre-resolved server user ID skip OPRF entirely.
    const uncached: ContactLike[] = [];
    for (const contact of contacts) {
      if (contact.userId) {
        if (!userIds.includes(contact.userId)) {
          userIds.push(contact.userId);
        }
      } else {
        uncached.push(contact);
      }
    }

    if (uncached.length === 0) {
      return { userIds, resolvedByPhone };
    }

    // OPRF-hash each uncached contact locally (isolating per-contact failures),
    // then resolve-or-create them all in one batch ensure call. A contact whose
    // hash fails is dropped (it can't be ensured without its hash).
    const hashOutcomes = await Promise.all(
      uncached.map(async contact => {
        const phoneStr = toPhoneString(contact.phone);
        try {
          const hashResult = await service.computeDeterministicHash(phoneStr);
          return { contact, phoneStr, hashResult, failed: false as const };
        } catch (err) {
          error(`Failed to compute hash for contact ${contact.name}:`, err);
          return { contact, phoneStr, hashResult: null, failed: true as const };
        }
      })
    );

    const reqs: EnsureUsersRequestItem[] = [];
    for (const outcome of hashOutcomes) {
      if (!outcome.failed) {
        reqs.push({
          phoneTail: outcome.hashResult.phoneTail,
          phoneOprf: outcome.hashResult.hash,
        });
      }
    }

    const idsByOprf =
      reqs.length > 0
        ? await service.ensureUsers(reqs)
        : new Map<string, string>();

    for (const outcome of hashOutcomes) {
      if (outcome.failed) continue;
      const id = idsByOprf.get(outcome.hashResult.hash);
      if (id) {
        if (!userIds.includes(id)) {
          userIds.push(id);
        }
        resolvedByPhone.set(outcome.phoneStr, id);
      }
    }

    return { userIds, resolvedByPhone };
  }

  async function resyncStaleContacts(
    contacts: ContactLike[],
    unknownUserIds: string[]
  ): Promise<{
    updatedContacts: ContactLike[];
    replacedUserIds: Record<string, string>;
  }> {
    const unknownSet = new Set<string>(unknownUserIds);
    const replacedUserIds: Record<string, string> = {};

    // Hash each stale contact (isolating failures), then re-ensure them all in a
    // single call, passing the stale id as the lastKnownUserId hint so the server
    // confirms or migrates it to a live row.
    const stale = contacts.filter(c => c.userId && unknownSet.has(c.userId));
    const hashOutcomes = await Promise.all(
      stale.map(async contact => {
        try {
          const { hash, phoneTail } = await service.computeDeterministicHash(
            toPhoneString(contact.phone)
          );
          return { contact, hash, phoneTail };
        } catch (err) {
          error(
            `Failed to resync user ID for ${contact.name}; dropping from retry:`,
            err
          );
          return null;
        }
      })
    );
    const resyncable = hashOutcomes.filter(
      (o): o is { contact: ContactLike; hash: string; phoneTail: string } =>
        o !== null
    );

    const idsByOprf =
      resyncable.length > 0
        ? await service.ensureUsers(
            resyncable.map(o => ({
              lastKnownUserId: o.contact.userId,
              phoneTail: o.phoneTail,
              phoneOprf: o.hash,
            }))
          )
        : new Map<string, string>();

    const newIdByContact = new Map<ContactLike, string>();
    for (const o of resyncable) {
      const newId = idsByOprf.get(o.hash);
      if (newId) {
        replacedUserIds[o.contact.userId as string] = newId;
        newIdByContact.set(o.contact, newId);
      }
    }

    // Stale contacts take their fresh id (or are dropped if unresynced — keeping
    // the stale ID would just cause another 422); everyone else passes through.
    const updatedContacts: ContactLike[] = [];
    for (const contact of contacts) {
      if (contact.userId && unknownSet.has(contact.userId)) {
        const newId = newIdByContact.get(contact);
        if (newId) {
          updatedContacts.push({ ...contact, userId: newId });
        }
      } else {
        updatedContacts.push(contact);
      }
    }

    return { updatedContacts, replacedUserIds };
  }

  async function ensureUsers(
    reqs: EnsureUsersRequestItem[]
  ): Promise<Map<string, string>> {
    const result = new Map<string, string>();
    if (reqs.length === 0) {
      return result;
    }
    const response = await apiCall(routes.ensure, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ users: reqs }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`ensureUsers failed: ${response.status} ${errorText}`);
    }
    const data = (await response.json()) as {
      users?: { oprf: string; id: string }[];
    };
    for (const user of data.users ?? []) {
      result.set(user.oprf, user.id);
    }
    return result;
  }

  async function getUserIdsByOprf(
    phoneOprfs: string[]
  ): Promise<Map<string, string>> {
    const result = new Map<string, string>();
    // De-dupe so the request carries each hash once; a caller may pass repeats.
    const uniqueOprfs = Array.from(new Set(phoneOprfs));
    if (uniqueOprfs.length === 0) {
      return result;
    }

    const response = await apiCall(routes.usersByOprfs, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ oprfs: uniqueOprfs }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `getUserIdsByOprf failed: ${response.status} ${errorText}`
      );
    }
    const data = (await response.json()) as {
      users?: { oprf: string; id: string }[];
    };
    for (const user of data.users ?? []) {
      result.set(user.oprf, user.id);
    }
    return result;
  }

  async function initiateSharingLeg({
    topicId,
    intent,
    type,
    recipients,
    updateSummary,
    aboutChangesSince,
    allowedSenderName,
    connectionToPrevLeg,
    previousLegId,
    allowedDetail,
    senderPronouns,
  }: MessageHeaderLike): Promise<InitiateSharingLegResponse> {
    const { userIds: allUserIds, resolvedByPhone } =
      await service._ensureContactsResolved(recipients);

    if (allUserIds.length === 0) {
      throw new Error('No users found or created for the selected contacts');
    }

    // phone → userId for every freshly-resolved (uncached) recipient — found or
    // created — so the wizard can cache the id back onto contacts as snowballId.
    const createdUserIds: Record<string, string> =
      Object.fromEntries(resolvedByPhone);

    const response = await apiCall(routes.legs, {
      method: 'POST',
      body: JSON.stringify({
        topic_id: topicId,
        type,
        intent,
        recipients: allUserIds,
        ...(updateSummary !== undefined ? { updateSummary } : {}),
        ...(aboutChangesSince !== undefined ? { aboutChangesSince } : {}),
        ...(allowedSenderName !== undefined ? { allowedSenderName } : {}),
        ...(connectionToPrevLeg !== undefined ? { connectionToPrevLeg } : {}),
        ...(previousLegId !== undefined ? { previousLegId } : {}),
        ...(allowedDetail !== undefined ? { allowedDetail } : {}),
        ...(senderPronouns !== undefined ? { senderPronouns } : {}),
      }),
    });

    if (response.status === 422) {
      const errorData = await response.json();
      if (errorData.error === 'unknown_user_ids') {
        const { updatedContacts, replacedUserIds } =
          await service._resyncStaleContacts(
            recipients,
            errorData.unknown_user_ids as string[]
          );
        const retryResult = await service.initiateSharingLeg({
          topicId,
          intent,
          type,
          recipients: updatedContacts,
          updateSummary,
          aboutChangesSince,
          allowedSenderName,
          connectionToPrevLeg,
          previousLegId,
          allowedDetail,
          senderPronouns,
        });
        return { ...retryResult, replacedUserIds };
      }
      // Any other 422 (e.g. intro_limit_exceeded): the body was already
      // consumed by response.json() above, so falling through to
      // response.text() below would throw "body stream already read" and mask
      // the real error. Throw from the parsed errorData instead — its `error`
      // code is what callers match on to show a friendly message.
      throw new Error(
        `Failed to create message leg: ${JSON.stringify(errorData)}`
      );
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create message leg: ${errorText}`);
    }

    const result = await response.json();
    return {
      ...(result as InitiateSharingLegResponse),
      ...(Object.keys(createdUserIds).length > 0 ? { createdUserIds } : {}),
    };
  }

  async function initiateSharingRun(
    topicId: string,
    story: {
      allowedSenderName?: string;
      allowedDetail?: string;
      senderPronouns?: string;
    } = {}
  ): Promise<InitiateSharingLegResponse> {
    // The empty starting leg (#455) reaches nobody, so there is nothing to resolve
    // via OPRF — POST it straight through with recipients: [] and no intent.
    // The backend stores a start leg with impression_count 0 that anchors the
    // run; its id becomes the previous leg of every leg the sharing flow goes
    // on to send.
    // Blank contributions are omitted rather than sent empty: the server
    // normalizes them to null anyway, and a sender may have given none of
    // them.
    const { allowedSenderName, allowedDetail, senderPronouns } = story;
    const response = await apiCall(routes.legs, {
      method: 'POST',
      body: JSON.stringify({
        topic_id: topicId,
        type: 'start',
        recipients: [],
        ...(allowedSenderName ? { allowedSenderName } : {}),
        ...(allowedDetail ? { allowedDetail } : {}),
        ...(senderPronouns ? { senderPronouns } : {}),
      }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to start sharing run: ${errorText}`);
    }
    return (await response.json()) as InitiateSharingLegResponse;
  }

  const LEG_STATUS_SENT = 'sent';
  const LEG_STATUS_IN_PROGRESS = 'in-progress';

  async function markLegAsSent(legId: string): Promise<void> {
    const response = await apiCall(routes.legById(legId), {
      method: 'PATCH',
      body: JSON.stringify({ status: LEG_STATUS_SENT }),
    });
    if (response.ok) return;
    const errorText = await response.text();
    throw new Error(`Failed to mark leg as sent: ${errorText}`);
  }

  async function markLegAsInProgress(legId: string): Promise<void> {
    const response = await apiCall(routes.legById(legId), {
      method: 'PATCH',
      body: JSON.stringify({ status: LEG_STATUS_IN_PROGRESS }),
    });
    if (response.ok) return;
    const errorText = await response.text();
    throw new Error(`Failed to mark leg as in-progress: ${errorText}`);
  }

  async function getSharingLeg(legId: string): Promise<SharingLegLike | null> {
    const response = await apiCall(routes.legById(legId));
    if (response.status === 404) return null;
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get leg: ${errorText}`);
    }
    return response.json() as Promise<SharingLegLike>;
  }

  async function getReshareInfoForLeg(
    legId: string
  ): Promise<ReshareInfoLike | null> {
    const response = await apiCall(`${routes.legById(legId)}/reshare-story`);
    // 404 represents a missing leg and 400 is a malformed id from a mangled link.
    if (response.status === 404 || response.status === 400) return null;
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get reshare story: ${errorText}`);
    }
    return response.json() as Promise<ReshareInfoLike>;
  }

  // Posts a story request to one of the two story endpoints.
  async function postStoryRequest(
    route: string,
    request: StoryRequest | FictionalStoryRequest
  ): Promise<ReshareInfoLike> {
    const response = await apiCall(route, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to render reshare story: ${errorText}`);
    }
    return response.json() as Promise<ReshareInfoLike>;
  }

  function renderReshareStory(request: StoryRequest): Promise<ReshareInfoLike> {
    return postStoryRequest(routes.story, request);
  }

  function renderReshareFictionalStory(
    request: FictionalStoryRequest
  ): Promise<ReshareInfoLike> {
    return postStoryRequest(routes.fictionalStory, request);
  }

  async function lookupLegTopicId(legId: string): Promise<string | null> {
    const response = await apiCall(routes.legTopicById(legId));
    if (response.status === 404) return null;
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to resolve leg topic: ${errorText}`);
    }
    const result = (await response.json()) as { topic_id: string };
    return result.topic_id;
  }

  async function recordLegClickThru(legId: string): Promise<void> {
    const response = await apiCall(`${routes.legById(legId)}/click`, {
      method: 'POST',
    });
    if (response.ok || response.status === 404) return;
    const errorText = await response.text();
    throw new Error(`Failed to record leg click: ${errorText}`);
  }

  async function getInvitePreview(
    topicId: string
  ): Promise<InvitePreview | null> {
    const response = await apiCall(routes.invitePreviewById(topicId));
    if (!response.ok) return null;
    const result = (await response.json()) as {
      type?: string;
      image?: string | null;
    };
    return { type: result.type ?? 'topic', image: result.image ?? null };
  }

  async function checkTopicAwareness(
    topicId: string,
    contacts: ContactLike[]
  ): Promise<TopicAwarenessResult> {
    const topicAwarenessMap: TopicAwarenessMap = new Map();
    const phoneToUserId = new Map<string, string>();

    try {
      // Short-circuit contacts that already have a pre-resolved server user ID
      const needsHash: ContactLike[] = [];
      for (const contact of contacts) {
        if (contact.userId) {
          phoneToUserId.set(toPhoneString(contact.phone), contact.userId);
        } else {
          needsHash.push(contact);
        }
      }

      if (needsHash.length > 0) {
        // OPRF-hash each uncached contact locally, then resolve them all to
        // server ids by exact hash in one batch lookup.
        const hashOutcomes = await Promise.all(
          needsHash.map(async contact => {
            const phoneStr = toPhoneString(contact.phone);
            try {
              const { hash: contactHash } =
                await service.computeDeterministicHash(phoneStr);
              return { contactHash, contact, failed: false as const };
            } catch (err) {
              error(`Failed to compute hash for contact ${contact.name}:`, err);
              return { contactHash: null, contact, failed: true as const };
            }
          })
        );

        const hashesToLookup = hashOutcomes
          .filter(o => !o.failed)
          .map(o => o.contactHash as string);
        const idsByHash =
          hashesToLookup.length > 0
            ? await service.getUserIdsByOprf(hashesToLookup)
            : new Map<string, string>();

        for (const outcome of hashOutcomes) {
          if (outcome.failed) continue;
          const userId = idsByHash.get(outcome.contactHash);
          if (userId) {
            phoneToUserId.set(toPhoneString(outcome.contact.phone), userId);
          }
        }
      }

      if (phoneToUserId.size === 0) {
        return { awareness: topicAwarenessMap, resolvedIds: phoneToUserId };
      }

      const searchParams = new URLSearchParams();
      const userIdsToCheck = Array.from(phoneToUserId.values());
      for (const userId of userIdsToCheck) {
        searchParams.append('userIds', userId);
      }
      const response = await apiCall(
        `${routes.topicAwareness(topicId)}?${searchParams.toString()}`
      );

      if (!response.ok) {
        throw new Error('Failed to check topic awareness');
      }

      const data = await response.json();
      const raw: TopicAwarenessMap = new Map(
        Object.entries(data.topicAwarenessMap ?? {})
      );
      raw.forEach((awareness, userId) => {
        topicAwarenessMap.set(userId, {
          ...awareness,
          introducedByAnyone:
            awareness.introducedByAnyone || awareness.reminderCount > 0,
        });
      });

      return { awareness: topicAwarenessMap, resolvedIds: phoneToUserId };
    } catch (err) {
      error('Failed to check topic awarenesses:', err);
      return { awareness: topicAwarenessMap, resolvedIds: phoneToUserId };
    }
  }

  async function matchMyPastRecipients(
    topicId: string,
    contacts: ContactLike[],
    before?: string,
    opts?: HashProgressOptions
  ): Promise<PastRecipientsResult> {
    const matches: PastRecipientMatch[] = [];
    const resolvedIds = new Map<string, string>();

    const response = await apiCall(
      routes.getPastShareRecipientsForTopic(topicId, before)
    );
    if (!response.ok) {
      throw new Error('Failed to fetch update candidates');
    }
    const data = (await response.json()) as {
      candidates?: UpdateCandidateLike[];
    };
    const candidates = data.candidates ?? [];
    if (candidates.length === 0 || contacts.length === 0) {
      return { matches, resolvedIds };
    }

    const byUserId = new Map<string, UpdateCandidateLike>();
    const byPhoneOprf = new Map<string, UpdateCandidateLike>();
    for (const candidate of candidates) {
      byUserId.set(candidate.userId, candidate);
      byPhoneOprf.set(candidate.phoneOprf, candidate);
    }

    // The candidate OPRFs still to be located. There are only ever a handful
    // (the recipients of the last share), while the address book can be huge —
    // so once this empties there is nothing left to find and any further OPRF
    // is wasted. Cached-userId matches below cross candidates off for free.
    const unmatchedOprfs = new Set<string>(byPhoneOprf.keys());

    // Contacts with a cached server userId match directly; the rest are
    // OPRF-hashed locally (batched, chunked) and intersected against the
    // candidates' phoneOprf values (the PSI step).
    const needsHash: ContactLike[] = [];
    for (const contact of contacts) {
      if (contact.userId) {
        const candidate = byUserId.get(contact.userId);
        if (candidate) {
          matches.push({ contact, userId: candidate.userId });
          unmatchedOprfs.delete(candidate.phoneOprf);
        }
      } else {
        needsHash.push(contact);
      }
    }

    // Every candidate already resolved via a cached userId — skip OPRF entirely.
    if (unmatchedOprfs.size === 0) {
      return { matches, resolvedIds };
    }

    if (needsHash.length > 0) {
      try {
        const hashes = await service.computeDeterministicHashes(
          needsHash.map(c => toPhoneString(c.phone)),
          {
            ...opts,
            // Stop hashing the moment every candidate has been located.
            onChunkComplete: chunk => {
              chunk.forEach(({ hash }) => unmatchedOprfs.delete(hash));
              return unmatchedOprfs.size === 0;
            },
          }
        );
        for (const contact of needsHash) {
          const phoneStr = toPhoneString(contact.phone);
          const hashResult = hashes.get(phoneStr);
          if (!hashResult) continue;
          const candidate = byPhoneOprf.get(hashResult.hash);
          if (candidate) {
            matches.push({ contact, userId: candidate.userId });
            resolvedIds.set(phoneStr, candidate.userId);
          }
        }
      } catch (err) {
        // Best-effort: userId-based matches above still stand.
        error('Failed to hash contacts for previous-recipient match:', err);
      }
    }

    return { matches, resolvedIds };
  }

  async function getReceivedUpdate(
    topicId: string
  ): Promise<ReceivedUpdateLike | null> {
    const response = await apiCall(routes.receivedUpdate(topicId));
    if (response.status === 404) return null;
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get received update: ${errorText}`);
    }
    const result = (await response.json()) as {
      legId: string;
      updateSummary?: string | null;
      aboutChangesSince?: string | null;
      sentAt: string;
    };
    return {
      legId: result.legId,
      updateSummary: result.updateSummary ?? undefined,
      aboutChangesSince: result.aboutChangesSince ?? undefined,
      sentAt: result.sentAt,
    };
  }

  const service: MePrFoService = {
    computeDeterministicHash,
    computeDeterministicHashes,
    matchMyPastRecipients,
    getReceivedUpdate,
    checkPhoneExists: async (phoneNumber: string) => {
      try {
        const { hash: phoneOprf, phoneTail } =
          await service.computeDeterministicHash(phoneNumber);
        const usersWithTail = await service._fetchUsersByTails([phoneTail]);
        const owner = usersWithTail.find(user => user.phoneOprf === phoneOprf);

        return { userId: owner ? owner.id : null, phoneOprf, phoneTail };
      } catch (err) {
        error('Failed to check phone existence:', err);
        throw err;
      }
    },
    initiateSharingLeg,
    initiateSharingRun,
    markLegAsSent,
    markLegAsInProgress,
    getSharingLeg,
    getReshareInfoForLeg,
    renderReshareStory,
    renderReshareFictionalStory,
    lookupLegTopicId,
    recordLegClickThru,
    getInvitePreview,
    ensureUsers,
    getUserIdsByOprf,
    _fetchUsersByTails: fetchUsersByTails,
    _ensureContactsResolved: ensureContactsResolved,
    _resyncStaleContacts: resyncStaleContacts,
    checkTopicAwareness,
  };

  return service;
}
